import { useEffect, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "./auth-context";
import { DataFetcher } from "../utils/dataFetcher";
import { getCachedData, setCachedData } from "../utils/dataCache";
import moment from "moment";
import { getJSON, getString, getBoolean } from "../utils/safeLocalStorage";

export const ProtectedRoute = ({ authCheck, children }) => {
  const effectRan = useRef(false);

  const navigate = useNavigate();
  const auth = useContext(AuthContext);

  const getUserData = async () => {
    auth.setLoading(true);
    if (!localStorage.getItem("year")) {
      localStorage.setItem("year", new Date().getFullYear());
    }

    const user = getJSON("user", {});
    const userId = user?._id;

    let response = null;

    // Check if cache bypass is enabled
    const bypassCache = getBoolean('bypassCache', false);
    
    if (userId && !bypassCache) {
      response = await getCachedData(userId, 5 * 60 * 1000);
    }

    if (!response) {
      response = await DataFetcher("run-all");
      
      if (response && userId && !bypassCache) {
        await setCachedData(userId, response);
      }
    }

    auth.setLoading(false);
    
    if (!response) {
      console.error("Failed to load user data");
      return;
    }

    auth.setTrades(response.trades || []);
    auth.setAllTrades(response.trades || []);
    auth.setBacktests(response.backtests || []);
    auth.setReflections(response.reflections || []);
    auth.setForecasts(response.forecasts || []);
    auth.setForecastsBacktesting(response.forecastsBacktesting || []);
    auth.setJournals(response.journals || []);
    auth.setJournalsBacktesting(response.journalsBacktesting || []);
    auth.setHolidays(response.holidays || []);

    const yearsSet = new Set();
    const currentYear = moment().year();
    const previousYear = currentYear - 1;
    
    yearsSet.add(previousYear.toString());
    yearsSet.add(currentYear.toString());
    
    response.trades.forEach((t) => {
      const year = moment(t.entryDate, "DD/MM/YY").format("YYYY");
      yearsSet.add(year);
    });

    const array_of_years_traded = Array.from(yearsSet).sort();

    auth.setAllYearsOfTrades(array_of_years_traded);

    auth.setTheme(getString("theme", "dark"));
    
    auth.setDefaultValue(
      auth.backtestingMode && getString("calendar-view-mode") === "Reflections"
        ? "All"
        : getString("calendar-view-mode")
    );
    
    auth.setBacktestingMode(getBoolean("backtest-mode", false));
    auth.setInProgressMode(getBoolean("inprogress-mode", false));
    auth.setViewMode(getString("view-mode"));

    localStorage.removeItem("trade-to-view");
    localStorage.removeItem("forecast-to-view");
    localStorage.removeItem("journal-portal");
    localStorage.removeItem("reflection-to-view");
    localStorage.removeItem("reflection-saved");
    localStorage.removeItem("from-tracker");
    localStorage.removeItem("view-date");
  };

  useEffect(() => {
    async function fetchData() {
      const auth_result = await authCheck();
      if (!auth_result) {
        navigate("/log-in");
      } else {
        await getUserData();
      }
    }

    if (effectRan.current === false) {
      fetchData();
      effectRan.current = true;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return children;
};
